import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';

export function useAntTableHorizontalScroll(containerRef, options = {}) {
    const scrollStep = options.scrollStep ?? 150;
    const hoverScrollStep = options.hoverScrollStep ?? 10;
    const hoverScrollIntervalMs = options.hoverScrollIntervalMs ?? 16;
    const selector = options.selector ?? '.ant-table-body';
    const additionalSelector= '.ant-table-content';

    const canScrollLeft = ref(false);
    const canScrollRight = ref(false);
    const hasHorizontalScroll = ref(false);

    const boundScrollEl = ref(null);
    let hoverScrollTimeout = null;
    let hoverScrollInterval = null;

    const getScrollEl = () => {
        return containerRef.value?.querySelector(selector) ?? containerRef.value?.querySelector(additionalSelector) ?? null;
    };

    const updateScrollState = () => {
        const el = getScrollEl();

        if (!el) {
            canScrollLeft.value = false;
            canScrollRight.value = false;
            hasHorizontalScroll.value = false;
            return;
        }

        const left = el.scrollLeft;
        const clientWidth = el.clientWidth;
        const scrollWidth = el.scrollWidth;

        canScrollLeft.value = left > 0;
        canScrollRight.value = left + clientWidth < scrollWidth - 1;
        hasHorizontalScroll.value = scrollWidth > clientWidth;
    };

    const bind = async () => {
        await nextTick();

        const el = getScrollEl();
        if (!el) return;

        if (boundScrollEl.value && boundScrollEl.value !== el) {
            boundScrollEl.value.removeEventListener('scroll', updateScrollState);
        }

        boundScrollEl.value = el;
        boundScrollEl.value.addEventListener('scroll', updateScrollState);
        updateScrollState();
    };

    const unbind = () => {
        if (boundScrollEl.value) {
            boundScrollEl.value.removeEventListener('scroll', updateScrollState);
            boundScrollEl.value = null;
        }
    };

    const rebind = async () => {
        unbind();
        await bind();
    };

    const scrollBy = (left, behavior = 'smooth') => {
        const el = boundScrollEl.value || getScrollEl();
        if (!el) return;

        el.scrollBy({
            left,
            behavior,
        });
    };

    const scrollLeft = () => {
        scrollBy(-scrollStep);
    };

    const scrollRight = () => {
        scrollBy(scrollStep);
    };

    const stopHoverScroll = () => {
        if (hoverScrollTimeout) {
            clearTimeout(hoverScrollTimeout);
            hoverScrollTimeout = null;
        }

        if (!hoverScrollInterval) return;

        clearInterval(hoverScrollInterval);
        hoverScrollInterval = null;
    };

    const startHoverScroll = (direction) => {
        stopHoverScroll();

        const scroll = () => {
            if (direction === 'left' && canScrollLeft.value) {
                scrollBy(-hoverScrollStep, 'auto');
                return;
            }

            if (direction === 'right' && canScrollRight.value) {
                scrollBy(hoverScrollStep, 'auto');
                return;
            }

            stopHoverScroll();
        };

        hoverScrollTimeout = setTimeout(() => {
            hoverScrollTimeout = null;
            scroll();
            hoverScrollInterval = setInterval(scroll, hoverScrollIntervalMs);
        }, 250);
    };

    onMounted(() => {
        window.addEventListener('resize', updateScrollState);
        bind();
    });

    onBeforeUnmount(() => {
        stopHoverScroll();
        unbind();
        window.removeEventListener('resize', updateScrollState);
    });

    return {
        showLeft: computed(() => hasHorizontalScroll.value && canScrollLeft.value),
        showRight: computed(() => hasHorizontalScroll.value && canScrollRight.value),
        scrollLeft,
        scrollRight,
        startHoverScroll,
        stopHoverScroll,
        updateScrollState,
        bindScroll: bind,
        unbindScroll: unbind,
        rebindScroll: rebind,
    };
}
